import './observability'
import './augment.d.ts'
import './index.css'
import './utils/sentry'
import './init' // Needs to be before App for proper DayJs initialization

import { OpenFeatureProvider } from '@openfeature/react-sdk'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { MotionConfig, MotionGlobalConfig } from 'motion/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import App from '~/app/App'
import { AstraTooltipProvider } from '~/astra'
import { AppBanner, Toaster } from '~/components'
import { initDipsSubscriptions } from '~/store/subscriptions/dipsSubscriptions'
import { reportQueryError } from '~/utils/sentry/reportQueryError'

import env from './env'
import { initializeFeatureFlags, subscribeToFeatureFlagRefresh } from './utils/featureFlags'

const rootElement = document.getElementById('root')

if (!rootElement) {
    throw new Error('Could not find root element')
}

if (env.VITE_NO_UNSTABLE_EFFECTS) {
    MotionGlobalConfig.skipAnimations = true
}

console.info(`Current version: ${env.VITE_GIT_TAG}${env.VITE_GIT_SHA ? `/${env.VITE_GIT_SHA}` : ''}`)

const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: reportQueryError }),
    defaultOptions: {
        queries: {
            // We use the store rather than the query cache to get data, and hence the default gcTime
            // of 5 minutes could cause unnecessary isLoading states by garbage collecting queries while
            // the data still exists in the store.
            gcTime: Infinity,
        },
    },
})

const AstraApp = () => (
    <OpenFeatureProvider>
        <div className="text-sm font-medium antialiased">
            <MotionConfig reducedMotion={env.VITE_NO_UNSTABLE_EFFECTS ? 'always' : 'user'}>
                <QueryClientProvider client={queryClient}>
                    <AppBanner />
                    <App />
                </QueryClientProvider>
                <Toaster />
                <AstraTooltipProvider />
            </MotionConfig>
        </div>
    </OpenFeatureProvider>
)

void initializeFeatureFlags()
    .then(provider => {
        if (provider) {
            subscribeToFeatureFlagRefresh(queryClient, provider)
        }
    })
    .catch(error => {
        console.error('Error initializing feature flags:', error)
    })
    .finally(() => {
        // initialize centralized store subscriptions once
        try {
            initDipsSubscriptions()
        } catch (err) {
            // initialization should not block app start

            console.error('Failed to init dips subscriptions', err)
        }
        createRoot(rootElement).render(
            env.VITE_STRICT_MODE ? (
                <StrictMode>
                    <AstraApp />
                </StrictMode>
            ) : (
                <AstraApp />
            )
        )
    })
